/**
 * Every CHECK list in D1 `core` (db/migrations 0001 and 0002, docs/53 §2.3), mirrored as zod enums.
 * "CHECK lists are the contract; packages/contracts mirrors them." A test in packages/db reads the applied
 * schema and fails if any list here drifts from the database, in either direction.
 */
import { z } from 'zod';
import { EVENT_METRICS } from './sites.ts';

export const DB_ENUMS = {
  // 0001
  'tenants.status': ['prospect', 'onboarding', 'live', 'paused', 'closed'],
  'tenants.audience': ['residential', 'commercial', 'both'],
  'hostnames.kind': ['custom', 'preview', 'platform'],
  'hostnames.status': ['pending', 'active', 'failed', 'removed'],
  'site_versions.status': ['rendering', 'ready', 'failed', 'retired'],
  'facts.status': ['verified', 'attested', 'unverified', 'expired'],
  'facts.source_kind': ['register', 'gbp', 'document', 'client'],
  'service_catalog.trade': ['plumbing', 'hvac'],
  'service_catalog.category': ['plumbing', 'drains-sewer', 'water-heaters', 'heating', 'cooling', 'indoor-air-quality', 'commercial'],
  'services.status': ['draft', 'active', 'retired'],
  'service_relations.kind': ['related', 'upsell'],
  'coverage.source': ['gbp', 'attested'],
  'site_dna.chosen_by': ['auto', 'client', 'will'],
  'page_copy.page_type': ['home'],
  // 0002
  'tenants.plan': ['monthly', 'yearly'],
  'tenants.approval_mode': ['we_handle_it', 'show_me_first'],
  'pods.kind': ['standard', 'watch'],
  'pods.status': ['ok', 'restricted', 'retired'],
  'locations.gbp_access': ['none', 'invite_sent', 'accepted', 'wrong_email', 'not_owner', 'ownership_request_open', 'removed'],
  'locations.status': ['onboarding', 'live', 'paused', 'ended'],
  'memberships.role': ['owner', 'manager', 'crew', 'bookkeeper'],
  'login_challenges.purpose': ['signin', 'step_up', 'email_change', 'deep_link'],
  'sessions.auth_method': ['passkey', 'email_code', 'email_link', 'invite'],
  'staff_users.role': ['owner', 'admin', 'operator', 'va', 'viewer'],
  'terms_acceptances.doc': ['terms', 'auto_renewal', 'ai_drafting', 'google_management', 'owner_authority', 'sms_attestation', 'dpa'],
  'notification_prefs.channel': ['push', 'email', 'sms'],
  'deliveries.subject_type': ['notification', 'lead', 'review_request', 'report'],
  'deliveries.channel': ['push', 'email', 'sms'],
  'deliveries.status': ['queued', 'sent', 'delivered', 'opened', 'clicked', 'failed', 'suppressed'],
  'onboarding_runs.status': ['active', 'live', 'refunded', 'extended', 'abandoned'],
  'onboarding_steps.step': ['pay', 'facts', 'google', 'domain', 'brand', 'photos', 'licence', 'go_live'],
  'onboarding_steps.state': ['todo', 'waiting_on_client', 'waiting_on_us', 'done', 'skipped'],
  'clock_intervals.kind': ['waiting_on_you', 'extension'],
  'findings.status': ['open', 'decision_pending', 'resolved', 'accepted_risk'],
  'domain_connects.status': ['discovering', 'waiting_records', 'validating', 'active', 'failed', 'rolled_back'],
  'leads.source': ['form', 'estimator', 'booking_click', 'manual', 'integration'],
  'leads.status': ['new', 'contacted', 'booked', 'won', 'lost', 'spam'],
  // One vocabulary since migration 0010: the names sites send to `/_e` (EVENT_METRICS in sites.ts), not a second set.
  'event_daily.metric': EVENT_METRICS,
  'reviews.reply_state': ['none', 'drafted', 'awaiting_client', 'approved', 'posted', 'not_replying'],
  'review_requests.source': ['app', 'lead', 'invoice_email', 'bulk', 'catchup', 'integration', 'owner_phone'],
  'review_requests.consent_basis': ['client_attested_job', 'integration_attested'],
  'review_requests.status': ['held', 'scheduled', 'sent', 'opened', 'clicked', 'failed', 'cancelled', 'suppressed', 'duplicate'],
  'suppressions.channel': ['sms', 'email', 'all'],
  'suppressions.reason': ['stop', 'unsubscribe', 'complaint', 'hard_bounce', 'landline', 'manual'],
  'messaging_profiles.brand_type': ['low_volume_standard', 'standard', 'sole_proprietor'],
  'messaging_profiles.status': ['not_started', 'preflight_failed', 'submitted', 'brand_approved', 'campaign_pending', 'active', 'rejected', 'suspended', 'released'],
  'messages.direction': ['out', 'in'],
  'messages.intent': ['opt_out', 'question', 'thanks', 'complaint', 'other'],
  'media_assets.source': ['app', 'email', 'mms', 'gbp', 'stock'],
  'media_assets.screen_state': ['pending', 'ok', 'flagged', 'rejected'],
  'jobs.status': ['received', 'drafting', 'in_review', 'awaiting_client', 'published', 'rejected'],
  'outputs.kind': ['site_project', 'gbp_post', 'gbp_photo', 'fb_post', 'ig_post', 'review_reply'],
  'outputs.state': ['drafted', 'in_review', 'approved', 'queued', 'live', 'removed', 'rejected'],
  'projects.status': ['draft', 'active', 'retired'],
  'redirects.source': ['crawl', 'search_console', 'backlinks', 'manual', '404_fix'],
  'requests.author_kind': ['user', 'staff', 'system', 'email'],
  'requests.channel': ['app', 'email', 'voice', 'system', 'console'],
  'requests.type': ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9'],
  'requests.status': ['new', 'triaged', 'drafting', 'awaiting_staff', 'awaiting_client', 'scheduled', 'live', 'closed', 'rejected'],
  'requests.severity': ['S1', 'S2', 'S3'],
  'requests.rating': ['sorted', 'not_yet'],
  'proposals.state': ['drafted', 'invalid', 'ready', 'staff_approved', 'client_approved', 'published', 'rolled_back', 'rejected'],
  'decisions.state': ['drafted', 'will_approved', 'sent', 'approved', 'declined', 'talk_first', 'executed', 'expired'],
  'approvals.subject_type': ['proposal', 'decision', 'go_live', 'reply', 'output', 'refund'],
  'approvals.approver_kind': ['user', 'staff'],
  'approvals.decision': ['approved', 'rejected', 'changes_requested'],
  'graduation.state': ['supervised', 'graduated', 'demoted', 'never'],
  'billing_accounts.status': ['setup_paid', 'card_saved', 'active', 'past_due', 'work_paused', 'canceling', 'ended', 'refunded'],
  'credits.kind': ['golive_miss', 'day90_refund', 'goodwill', 'duplicate', 'closure'],
  'ops_alerts.severity': ['sev1', 'sev2', 'sev3'],
  'guarantee_reads.kind': ['golive', 'day90'],
  'guarantee_reads.result': ['met', 'missed', 'qualifies_refund', 'not_qualified'],
  'grid_runs.purpose': ['baseline', 'monthly', 'day90', 'regrid', 'prospect'],
  'grid_runs.status': ['queued', 'running', 'complete', 'partial', 'held', 'failed'],
  'reports.status': ['drafting', 'human_read', 'published'],
  'oauth_tokens.provider': ['google', 'meta', 'jobber', 'housecall_pro', 'servicetitan'],
  'inbound_addresses.kind': ['photos', 'jobs', 'requests'],
  'known_senders.channel': ['email', 'mms'],
  'exports.kind': ['everything', 'site_static', 'audit'],
  'exports.status': ['queued', 'building', 'ready', 'expired', 'failed'],
  'audit_events.actor_kind': ['user', 'staff', 'system', 'agent', 'mcp', 'webhook'],
  'state_trade_rules.status': ['unverified', 'draft', 'verified', 'stale'],
  // 0003
  'uploads.kind': ['photo', 'video', 'voice', 'document'],
  'uploads.purpose': ['job', 'insurance', 'bulk_sheet', 'request_attachment', 'logo', 'brand_note'],
  'uploads.status': ['open', 'complete', 'processing', 'ready', 'rejected', 'expired'],
  'fact_confirmations.decision': ['confirm', 'edit', 'not_sure'],
  'gbp_tasks.subject_type': ['output', 'proposal', 'decision'],
  'gbp_tasks.state': ['open', 'done', 'problem'],
  // 0006
  'review_templates.kind': ['request', 'reminder'],
  // 0007
  'no_jobs_marks.period': ['week', 'month'],
  // 0010 (scans, reports and the event roll up)
  'scan_grids.basis': ['coverage_radius', 'coverage_towns', 'default', 'staff'],
  'grid_points.status': ['ok', 'failed'],
  // The AI Overview check (R6-03, migration 0011): one per scan run.
  'scan_ai_overviews.status': ['ok', 'failed'],
  // 0008 (the photo pipeline, k11)
  'media_assets.kind': ['work', 'before', 'after', 'people', 'premises', 'vehicle', 'logo'],
  'media_assets.kind_source': ['client', 'staff', 'job', 'ai'],
  // 0009 (review engine v1, k10)
  'review_requests.entry': ['form', 'quick', 'lead', 'paste', 'csv'],
  'review_taps.source': ['ask', 'link', 'card'],
  'global_suppressions.reason': ['hard_bounce', 'complaint', 'provider', 'manual'],
  // 0012 (the support desk, SUP-D1)
  'support_tickets.source': ['app', 'email', 'console'],
  'support_tickets.state': ['preparing', 'ready', 'approved', 'sent', 'closed', 'failed'],
  'support_tickets.route': ['change', 'checklist', 'person'],
  'support_tickets.risk': ['normal', 'hold'],
  'support_tickets.reply_source': ['ai', 'template', 'staff', 'none'],
} as const satisfies Record<string, readonly string[]>;

export type DbEnumKey = keyof typeof DB_ENUMS;

// Named enums used across the contracts. Each is built from DB_ENUMS so there's one list per concept.
export const TenantStatus = z.enum(DB_ENUMS['tenants.status']);
export const Plan = z.enum(DB_ENUMS['tenants.plan']);
export const ApprovalMode = z.enum(DB_ENUMS['tenants.approval_mode']);
export const Trade = z.enum(DB_ENUMS['service_catalog.trade']);
export const FactStatus = z.enum(DB_ENUMS['facts.status']);
export const FactSourceKind = z.enum(DB_ENUMS['facts.source_kind']);
export const GbpAccess = z.enum(DB_ENUMS['locations.gbp_access']);
export const LocationStatus = z.enum(DB_ENUMS['locations.status']);
export const ClientRole = z.enum(DB_ENUMS['memberships.role']);
export const StaffRole = z.enum(DB_ENUMS['staff_users.role']);
export const AuthMethod = z.enum(DB_ENUMS['sessions.auth_method']);
export const ChallengePurpose = z.enum(DB_ENUMS['login_challenges.purpose']);
export const TermsDoc = z.enum(DB_ENUMS['terms_acceptances.doc']);
export const Channel = z.enum(DB_ENUMS['deliveries.channel']);
export const DeliverySubjectType = z.enum(DB_ENUMS['deliveries.subject_type']);
export const DeliveryStatus = z.enum(DB_ENUMS['deliveries.status']);
export const OnboardingRunStatus = z.enum(DB_ENUMS['onboarding_runs.status']);
export const OnboardingStep = z.enum(DB_ENUMS['onboarding_steps.step']);
export const OnboardingStepState = z.enum(DB_ENUMS['onboarding_steps.state']);
export const DomainConnectStatus = z.enum(DB_ENUMS['domain_connects.status']);
export const LeadSource = z.enum(DB_ENUMS['leads.source']);
export const LeadStatus = z.enum(DB_ENUMS['leads.status']);
export const EventMetric = z.enum(DB_ENUMS['event_daily.metric']);
export const ReplyState = z.enum(DB_ENUMS['reviews.reply_state']);
export const ReviewRequestSource = z.enum(DB_ENUMS['review_requests.source']);
export const ConsentBasis = z.enum(DB_ENUMS['review_requests.consent_basis']);
export const ReviewRequestStatus = z.enum(DB_ENUMS['review_requests.status']);
export const ReviewRequestEntry = z.enum(DB_ENUMS['review_requests.entry']);
export const ReviewTapSource = z.enum(DB_ENUMS['review_taps.source']);
export type ReviewTapSource = z.infer<typeof ReviewTapSource>;
export const GlobalSuppressionReason = z.enum(DB_ENUMS['global_suppressions.reason']);
export type GlobalSuppressionReason = z.infer<typeof GlobalSuppressionReason>;
export const SuppressionChannel = z.enum(DB_ENUMS['suppressions.channel']);
export const SuppressionReason = z.enum(DB_ENUMS['suppressions.reason']);
export const MessagingProfileStatus = z.enum(DB_ENUMS['messaging_profiles.status']);
export const BrandType = z.enum(DB_ENUMS['messaging_profiles.brand_type']);
export const MessageDirection = z.enum(DB_ENUMS['messages.direction']);
export const MessageIntent = z.enum(DB_ENUMS['messages.intent']);
export const MediaSource = z.enum(DB_ENUMS['media_assets.source']);
export const ScreenState = z.enum(DB_ENUMS['media_assets.screen_state']);
export const JobStatus = z.enum(DB_ENUMS['jobs.status']);
export const OutputKind = z.enum(DB_ENUMS['outputs.kind']);
export const OutputState = z.enum(DB_ENUMS['outputs.state']);
export const RequestAuthorKind = z.enum(DB_ENUMS['requests.author_kind']);
export const RequestChannel = z.enum(DB_ENUMS['requests.channel']);
export const RequestType = z.enum(DB_ENUMS['requests.type']);
export const RequestStatus = z.enum(DB_ENUMS['requests.status']);
export const Severity = z.enum(DB_ENUMS['requests.severity']);
export const RequestRating = z.enum(DB_ENUMS['requests.rating']);
export const ProposalState = z.enum(DB_ENUMS['proposals.state']);
export const SupportSource = z.enum(DB_ENUMS['support_tickets.source']);
export const SupportState = z.enum(DB_ENUMS['support_tickets.state']);
export const SupportRoute = z.enum(DB_ENUMS['support_tickets.route']);
export const SupportRisk = z.enum(DB_ENUMS['support_tickets.risk']);
export const SupportReplySource = z.enum(DB_ENUMS['support_tickets.reply_source']);
export const DecisionState = z.enum(DB_ENUMS['decisions.state']);
export const ApprovalSubjectType = z.enum(DB_ENUMS['approvals.subject_type']);
export const ApproverKind = z.enum(DB_ENUMS['approvals.approver_kind']);
export const ApprovalOutcome = z.enum(DB_ENUMS['approvals.decision']);
export const GraduationState = z.enum(DB_ENUMS['graduation.state']);
export const BillingStatus = z.enum(DB_ENUMS['billing_accounts.status']);
export const CreditKind = z.enum(DB_ENUMS['credits.kind']);
export const GuaranteeKind = z.enum(DB_ENUMS['guarantee_reads.kind']);
export const GuaranteeResult = z.enum(DB_ENUMS['guarantee_reads.result']);
export const GridPurpose = z.enum(DB_ENUMS['grid_runs.purpose']);
export const GridRunStatus = z.enum(DB_ENUMS['grid_runs.status']);
export type GridRunStatus = z.infer<typeof GridRunStatus>;
export type GridPurpose = z.infer<typeof GridPurpose>;
export const ReportStatus = z.enum(DB_ENUMS['reports.status']);
export const OAuthProvider = z.enum(DB_ENUMS['oauth_tokens.provider']);
export const InboundKind = z.enum(DB_ENUMS['inbound_addresses.kind']);
export const ExportKind = z.enum(DB_ENUMS['exports.kind']);
export const ExportStatus = z.enum(DB_ENUMS['exports.status']);
export const ActorKind = z.enum(DB_ENUMS['audit_events.actor_kind']);
export const SiteVersionStatus = z.enum(DB_ENUMS['site_versions.status']);
export const HostnameStatus = z.enum(DB_ENUMS['hostnames.status']);
export const HostnameKind = z.enum(DB_ENUMS['hostnames.kind']);
export const RuleStatus = z.enum(DB_ENUMS['state_trade_rules.status']);
export const PodStatus = z.enum(DB_ENUMS['pods.status']);

export type OnboardingStep = z.infer<typeof OnboardingStep>;
export type ClientRole = z.infer<typeof ClientRole>;
export type StaffRole = z.infer<typeof StaffRole>;
export type ActorKind = z.infer<typeof ActorKind>;
export type ExportKind = z.infer<typeof ExportKind>;
export type LeadStatus = z.infer<typeof LeadStatus>;
export type Channel = z.infer<typeof Channel>;

/** Languages a review request can go out in (English at v1, Spanish templates at v1.1). */
export const Language = z.enum(['en', 'es']);

/** Where a write came from (audit_events.via). Free text in the DDL, fixed here. */
export const Via = z.enum(['app', 'console', 'agent', 'mcp', 'webhook', 'system', 'email', 'sites']);
export type Via = z.infer<typeof Via>;

/** Weekdays for hours grids. */
export const Weekday = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
